import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import Button from "../Components/Button";
import Input from "../Components/Input";
import ReportsLayout from "./ReportsLayout";
import { NotificationManager } from "react-notifications";
import { UserContext } from "../Auth/UserContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTrash, faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import useAllEventsFilterDate from "../hooks/useAllEventsFilterDate";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import useApi from "../Api/Api";
import useDateSelections from "../hooks/useDateSelections";
import Select from "react-select";
import { Link } from "react-router-dom";
import buildSaveErrorMessage from "./buildSaveErrorMessage";

// Need to reload if expenses or payments change

const usePartnershipReport = () => {
  const api = useApi();
  const [report, setReport] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const {
    filterData: { artist_id, start_date, end_date },
  } = useContext(FilterContext);

  const fetchReport = useCallback(() => {
    setIsFetching(true);
    artist_id &&
      api
        .get(`/reports/partnership?artist_id=${artist_id}&start_date=${start_date}&end_date=${end_date}}`)
        .then((r) => {
          setReport(r.data);
          setIsFetching(false);
        })
        .catch((error) => {
          setIsFetching(false);
          console.log("usePartnershipReport error", error);
        });
  }, [api, artist_id, start_date, end_date]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    isFetching,
    fetchReport,
  };
};

const Partnership = () => {
  const { isAdmin } = useContext(UserContext);
  const { filterData } = useContext(FilterContext);
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { allEventsFilterDate: allEvents } = useAllEventsFilterDate("show");
  const [artistFees, setArtistFees] = useState([]);
  const [artistPayments, setArtistPayments] = useState([]);
  const [artistSplits, setArtistSplits] = useState([]);
  const [showButtons, setShowButtons] = useState(false);
  const [hideButtons, setHideButtons] = useState(false);
  const [editFees, setEditFees] = useState(false);
  const [editPayments, setEditPayments] = useState(false);
  const [artist, setArtist] = useState({});
  const printRef = useRef();
  const api = useApi();
  const { dateOptions, handleDateSelect, renderCustomDateSelector } = useDateSelections();
  const { report, isFetching: isFetchingReport, fetchReport } = usePartnershipReport();

  const feeOptions = [
    { label: "Payroll", value: "Payroll" },
    { label: "Commission", value: "Commission" },
    { label: "FICA", value: "FICA" },
    { label: "Bonus", value: "Bonus" },
    { label: "Freight", value: "Freight" },
    { label: "Print Charges", value: "Print Charges" },
    { label: "Other", value: "Other" },
  ];

  const artistFee = useState({
    description: "test",
    amount: "20",
    date: "",
    before_net: true,
  });

  const artistPayment = useState({
    description: "",
    amount: "",
    date: "",
  });

  const getArtist = useCallback(() => {
    api.get(`/artists/${filterData.artist_id}`).then((r) => {
      setArtist(r.data);
    });
  }, [api, filterData]);

  const getArtistFees = useCallback(() => {
    api
      .get(
        `/artist_fees?artist_id=${filterData.artist_id}&start_date=${filterData.start_date}&end_date=${filterData.end_date}`
      )
      .then((r) => {
        setArtistFees(r.data);
      });
  }, [api, filterData]);

  useEffect(() => {
    getArtist();
    getArtistFees();
  }, [getArtist, getArtistFees]);

  const getArtistPayments = useCallback(() => {
    api
      .get(
        `/artist_payments?artist_id=${filterData.artist_id}&start_date=${filterData.start_date}&end_date=${filterData.end_date}`
      )
      .then((r) => {
        setArtistPayments(r.data);
      });
  }, [api, filterData]);

  useEffect(() => {
    getArtistPayments();
  }, [getArtistPayments]);

  useEffect(() => {
    api.get(`/artist_splits?artist_id=${filterData.artist_id}`).then((r) => {
      setArtistSplits(r.data);
    });
  }, [api, filterData.artist_id]);

  allEvents.forEach((event) => {
    if (!event.tax_kept_by_venue && !event.remitted) {
      event.tax_not_kept = 0;
    } else {
      event.tax_not_kept = event.tax_net;
    }
  });

  const addFee = (e) => {
    setShowButtons(true);
    setEditFees(true);
    e.preventDefault();
    const newFee = { ...artistFee, before_net: true };
    setArtistFees([...artistFees, newFee]);
  };

  const addPayment = (e) => {
    setShowButtons(true);
    setEditPayments(true);
    e.preventDefault();
    const newPayment = { ...artistPayment };
    setArtistPayments([...artistPayments, newPayment]);
  };

  const handleFeeChange = (e, i) => {
    const newFees = artistFees.map((obj, j) =>
      j === i ? { ...obj, [e.target.id]: e.target.value, toAdd: true } : obj
    );
    setArtistFees(newFees);
  };

  const handleFeeTypeSelect = (i, option) => {
    const newFees = artistFees.map((obj, j) => (j === i ? { ...obj, description: option.value, toAdd: true } : obj));
    setArtistFees(newFees);
  };

  const handlePaymentChange = (e, i) => {
    const newPayments = artistPayments.map((obj, j) =>
      j === i ? { ...obj, [e.target.id]: e.target.value, toAdd: true } : obj
    );
    setArtistPayments(newPayments);
  };

  const handleBooleanChange = (state, i) => {
    const newFees = artistFees.map((obj, j) => (j === i ? { ...obj, before_net: state } : obj));
    setArtistFees(newFees);
  };

  const handleHideButtons = () => {
    if (editFees || editPayments) {
      window.alert("You have pending changes, either revert or save them to print.");
    } else {
      setHideButtons(!hideButtons);
    }
  };

  const feesToAdd = artistFees.filter((each) => each.toAdd);
  const paymentsToAdd = artistPayments.filter((each) => each.toAdd);

  const handleSubmit = (event) => {
    event.preventDefault();
    artistFees.forEach((one) => {
      if (!one.other_description) {
        one.other_description = one.description;
      }
    });
    setIsFetching(true);
    api
      .put(`/artists/${filterData.artist_id}`, {
        artist: {
          artist_fees_attributes: feesToAdd.map((each) => ({
            ...each,
            artist_id: filterData.artist_id,
            description: each.other_description,
          })),
          artist_payments_attributes: paymentsToAdd.map((each) => ({
            ...each,
            artist_id: filterData.artist_id,
          })),
        },
      })
      .then((response) => {
        NotificationManager.success("Fees and payments successfully updated", "Success", 2000);
        getArtistFees();
        getArtistPayments();
        fetchReport();
        setEditFees(false);
        setEditPayments(false);
        setShowButtons(false);
        return response.data;
      })
      .catch((error) => {
        setErrorMessage(error.response?.data || {});
        NotificationManager.error(buildSaveErrorMessage(error), "Could not save", 6000);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const revert = () => {
    const check = window.confirm("Are you sure you want to revert? All unsaved changes will be lost.");
    if (check) {
      getArtistFees();
      getArtistPayments();
      setEditFees(false);
      setEditPayments(false);
      setShowButtons(false);
    }
  };

  const deleteFee = (e, feeToDelete) => {
    e.preventDefault();
    const check = window.confirm(`Are you sure you want to delete artist split from ${feeToDelete.description}?`);
    if (check) {
      api.delete(`artist_fees/${feeToDelete.id}`).then(() => {
        getArtistFees();
        fetchReport();
      });
    }
  };

  const deletePayment = (e, paymentToDelete) => {
    e.preventDefault();
    const check = window.confirm(`Are you sure you want to delete artist split from ${paymentToDelete.description}?`);
    if (check) {
      api.delete(`artist_payments/${paymentToDelete.id}`).then(() => {
        getArtistPayments();
        fetchReport();
      });
    }
  };

  allEvents.forEach((each) => {
    if (each.vendor === "Artist") {
      each.payroll = artist.manager_payment;
    } else if (each.vendor === "Venue") {
      each.payroll = artist.venue_vended_manager_payment;
    } else {
      each.payroll = 0;
    }
  });

  const payroll = allEvents
    .reduce((a, b) => {
      return a + b.payroll;
    }, 0)
    ?.toFixed(2);

  const commission = (
    allEvents
      .filter((each) => each.vendor === "Artist")
      .reduce((a, b) => {
        return a + (b.gross_sales - b.tax_not_kept);
      }, 0) * artist.manager_commission_percentage
  )?.toFixed(2);

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        <div className="flex justify-end text-sm">
          {hideButtons && (
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
          )}
          {!hideButtons && <Button className="button" text="Format Print" onClick={() => handleHideButtons()}></Button>}
        </div>
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/4">
          <span className="text-xs">SELECT DATE RANGE</span>
          <Select
            id="date"
            className="text-xs font-light w-full"
            placeholder="Select date range"
            options={dateOptions}
            onChange={handleDateSelect}
            value={dateOptions?.filter((each) => each.value === filterData.date_button)}
          />
        </div>
        <div className="my-4 w-1/2">{filterData.date_button === "custom" && renderCustomDateSelector()}</div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-base mb-2 border-2 border-black p-2">
            <div className="w-1/3 text-xl font-extrabold mr-4">TM Partnership Report</div>
            <div className="w-1/3">
              <div className="text-lg flex self-center">
                <div className="mr-2 italic">Artist:</div>
                {filterData.artist_name}
              </div>
            </div>
            <div className=" w-1/3">
              <div className="self-center flex font-normal">
                <div className="mr-2 italic">Start Date:</div>
                {filterData.start_date?.slice(5, 7)}/{filterData.start_date?.slice(8, 10)}/
                {filterData.start_date?.slice(0, 4)}
              </div>
              <div className="self-center flex font-normal">
                <div className=" mr-4 italic">End Date:</div>
                {filterData.end_date?.slice(5, 7)}/{filterData.end_date?.slice(8, 10)}/
                {filterData.end_date?.slice(0, 4)}
              </div>
            </div>
          </div>
          <div className="flex mb-2 border-2 border-black p-2 max-w-max text-right">
            {isFetchingReport ? (
              <FontAwesomeIcon icon={faSpinner} spin></FontAwesomeIcon>
            ) : (
              <div className="flex justify-between">
                <div>
                  <div className="font-bold">Sales:</div>
                  <div>Tax:</div>
                  <div>Event Fees:</div>
                  <div>CC Fee:</div>
                  <div>Hall Fee:</div>
                  <div className="font-bold">Net Sales:</div>
                  <div>Cost of Goods Sold:</div>
                  <div>Total Before Net Fees:</div>
                  <div>Less Damage Inventory:</div>
                  <div>Profit from Discount Sales:</div>
                  <div>Total Adjusted Gross:</div>
                  <div className={artistSplits.length ? "font-bold" : "font-bold text-red-500"}>
                    {artistSplits.length
                      ? (
                          ((report?.artist_split_of_total_adjusted_gross ?? 0) / (report?.total_adjusted_gross ?? 1)) *
                          100
                        ).toFixed(2)
                      : "No split"}
                    % Net Profit:
                  </div>
                  <div>Less Artist Comp:</div>
                  <div>Less Mail Order:</div>
                  <div>Less Management Comp:</div>
                  <div>Total After Net Fees:</div>
                  <div className="font-bold">Net to Artist:</div>
                  <div className="font-bold">Payments:</div>
                  <div className="font-bold">Balance:</div>
                </div>
                <div className="ml-8">
                  <div id="gross_sales" className="font-bold">
                    {parseFloat(report?.gross_sales || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="tax_net">
                    {parseFloat(report?.tax || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="fee_net">
                    {parseFloat(report?.fee_net || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="cc_net">
                    {parseFloat(report?.cc_net || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="hall_net">
                    {parseFloat(report?.hall_net || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="net_sales" className="font-bold">
                    {parseFloat(report?.net_sales || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_cogs">
                    {parseFloat(report?.cost_of_goods_sold || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_before_net_fees">
                    {parseFloat(report?.total_before_net_fees || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_damage">
                    {parseFloat(report?.damage_quantity_cost ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="profit_from_discount_sales">
                    {parseFloat(report?.profit_from_discount_sales || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_adjusted_gross">
                    {parseFloat(report?.total_adjusted_gross ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="net_profit" className="font-bold">
                    {parseFloat(report?.artist_split_of_total_adjusted_gross || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_artist_comp">
                    {parseFloat(report?.artist_comp_cost ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_mail_order">
                    {parseFloat(report?.total_mail_order_cost ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_management_comp">
                    {parseFloat(report?.management_comp_cost ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_after_net_fees">
                    {parseFloat(report?.total_after_net_fees ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="net_to_artist" className="font-bold">
                    {parseFloat(report?.net_to_artist ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="total_payment" className="font-bold">
                    {parseFloat(report?.total_payments ?? 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div id="balance" className="font-bold">
                    {parseFloat(report?.balance ?? 0)?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div id="expenses" className="mt-8">
            <div className="text-lg italic font-semibold">Expenses</div>
            <div className="flex justify-between mb-2 underline">
              <div className="w-3/4 flex">
                <div className="w-1/3">Date</div>
                <div className="w-1/2">Description</div>
                <div className="w-1/6 text-right">Amount</div>
              </div>
              <div className="w-1/4 flex">
                <div className="w-1/5"></div>
                <div className="w-2/5 text-center">Before Net Fee</div>
                {editFees && <div className="w-2/5 text-center">Action</div>}
              </div>
            </div>
            {artistFees.map((each, i) => (
              <div key={i} id={`artist_fee_${i}`}>
                {!editFees ? (
                  <div className="flex justify-between mt-1">
                    <div className="w-3/4 flex border-b border-black">
                      <div className="w-1/3">
                        {each.date?.slice(5, 7)}/{each.date?.slice(8, 10)}/{each.date?.slice(0, 4)}
                      </div>
                      <div className="w-1/2">{each?.description}</div>
                      <div className="w-1/6 text-right">
                        {parseFloat(each?.amount)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                    </div>
                    <div className="w-1/4 flex">
                      <div className="w-1/5  border-b border-black"></div>
                      <div className="w-2/5 text-center border-b border-black">
                        {each?.before_net && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <form className="w-full flex justify-between my-1" id="submit">
                    <div className="w-3/4 flex">
                      <div className="w-1/3">
                        <input
                          value={each.date?.split("T")[0] ?? ""}
                          type="date"
                          id="date"
                          name="date"
                          className="h-9 pl-2 rounded-md border w-5/6"
                          onChange={(e) => handleFeeChange(e, i)}
                          required
                        ></input>
                      </div>
                      <div className="w-1/2">
                        <div className="w-5/6">
                          <Select
                            id="description"
                            className="text-xs font-light"
                            placeholder={
                              feeOptions?.find((option) => option.value === each.description) ?? each.description
                            }
                            options={feeOptions}
                            onChange={(option) => handleFeeTypeSelect(i, option)}
                            value={feeOptions?.filter((option) => option.value === each.description)}
                            required
                          />
                        </div>
                      </div>
                      {each.description === "Other" && (
                        <div className="w-1/2">
                          <div className="w-5/6">
                            <Input
                              id="other_description"
                              type="other_description"
                              label="Other other_Description"
                              hideLabel
                              value={each?.other_description ?? ""}
                              name="other_description"
                              onChange={(e) => handleFeeChange(e, i)}
                              disabled={isFetching}
                              required
                            />
                          </div>
                        </div>
                      )}
                      <div className="w-1/6">
                        <div className="float-right text-right">
                          <Input
                            id="amount"
                            type="amount"
                            label="Amount"
                            placeholder={
                              each.description === "Payroll"
                                ? payroll
                                : each.description === "Commission"
                                ? commission
                                : ""
                            }
                            hideLabel
                            value={each?.amount ?? ""}
                            name="amount"
                            onChange={(e) => handleFeeChange(e, i)}
                            disabled={isFetching}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-1/4 flex">
                      <div className="w-1/5"></div>
                      <input
                        type="checkbox"
                        id="before_net"
                        checked={!!each?.before_net}
                        onChange={() => handleBooleanChange(!each?.before_net, i)}
                        className="w-2/5 text-center self-center"
                      />
                      {isAdmin && (
                        <>
                          {each.id ? (
                            <button className="w-2/5" onClick={(e) => deleteFee(e, each)}>
                              <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon>
                            </button>
                          ) : (
                            <div className="w-2/5"></div>
                          )}
                        </>
                      )}
                    </div>
                  </form>
                )}
              </div>
            ))}
            <div className="mt-2"></div>
            {!hideButtons && (
              <Button
                text="Add / Edit"
                disabled={isFetching}
                className="button mr-2"
                onClick={(e) => addFee(e)}
              ></Button>
            )}
          </div>
          <div id="payments" className="mt-8">
            <div className="text-lg italic font-semibold">Payments</div>
            <div className="flex justify-between items-end mb-2 underline">
              <div className="w-3/4 flex">
                <div className="w-1/3">Date</div>
                <div className="w-1/2">Description</div>
                <div className="w-1/6 text-right">Amount</div>
              </div>
              {editPayments && <div className="w-1/10 text-center">Action</div>}
            </div>
            {artistPayments.map((each, i) => (
              <div key={i} id={`artist_payment_${i}`}>
                {!editPayments ? (
                  <div className="w-3/4 flex justify-between mt-1 border-b border-black">
                    <div className="w-1/3">
                      {each.date?.slice(5, 7)}/{each.date?.slice(8, 10)}/{each.date?.slice(0, 4)}
                    </div>
                    <div className="w-1/2">{each?.description}</div>
                    <div className="w-1/6 text-right">
                      {parseFloat(each?.amount)?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </div>
                  </div>
                ) : (
                  <form className="w-full flex justify-between my-1" id="submit">
                    <div className="w-3/4 flex">
                      <div className="w-1/3">
                        <input
                          value={each.date?.split("T")[0] ?? ""}
                          type="date"
                          id="date"
                          name="date"
                          className="h-9 pl-2 rounded-md border w-5/6"
                          onChange={(e) => handlePaymentChange(e, i)}
                          required
                        ></input>
                      </div>
                      <div className="w-1/2">
                        <div className="w-5/6">
                          <Input
                            id="description"
                            type="description"
                            label="Description"
                            hideLabel
                            value={each?.description ?? ""}
                            name="description"
                            onChange={(e) => handlePaymentChange(e, i)}
                            disabled={isFetching}
                            required
                          />
                        </div>
                      </div>
                      <div className="w-1/6">
                        <div className="float-right text-right">
                          <Input
                            id="amount"
                            type="amount"
                            label="Amount"
                            hideLabel
                            value={each?.amount ?? ""}
                            name="amount"
                            onChange={(e) => handlePaymentChange(e, i)}
                            disabled={isFetching}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    {isAdmin && (
                      <>
                        {each.id ? (
                          <button className="w-1/10" onClick={(e) => deletePayment(e, each)}>
                            <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon>
                          </button>
                        ) : (
                          <div className="w-1/10"></div>
                        )}
                      </>
                    )}
                    {errorMessage?.errors?.description && (
                      <div className="text-red-500 pt-2">Description {errorMessage.errors.description}</div>
                    )}
                  </form>
                )}
              </div>
            ))}
            <div className="mt-2">
              {!hideButtons && (
                <Button
                  text="Add / Edit"
                  disabled={isFetching}
                  className="button mr-2"
                  onClick={(e) => addPayment(e)}
                ></Button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 flex justify-center">
          {showButtons && (
            <>
              <span className="mr-2">
                <Button className="button button-white" onClick={revert} text="Revert"></Button>
              </span>
              <Button text="Update" disabled={isFetching} className="button mr-2" onClick={handleSubmit}></Button>
            </>
          )}
          {hideButtons && (
            <div className="mt-4">
              <Button text="Edit" disabled={isFetching} className="button mr-2" onClick={handleHideButtons}></Button>
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

export default Partnership;
